/**
 * Per-website color palette — designed for MAXIMUM perceptual distance.
 *
 * Constraints honored:
 *   - NO red / rose / pink anywhere (reserved for reject + failed pills)
 *   - NO near-shade pairs (no two greens, no two blues, no two purples)
 *   - Every site shows a 2-letter initial badge ALWAYS, so even sites
 *     that share a hue family (bright vs dark variants) are instantly
 *     distinguishable by their letter code
 *
 * Base palette = 6 truly distinct hues. Each consecutive pair is at
 * least ~60° apart on the HSL wheel:
 *
 *   idx 0  EMERALD  green        158°
 *   idx 1  AMBER    yellow-amber  38°
 *   idx 2  INDIGO   deep blue    244°
 *   idx 3  FUCHSIA  magenta      293°
 *   idx 4  SLATE    cool grey   ~215° desat
 *   idx 5  BROWN    earth/sienna  25° desat
 *
 * Dark ring (slots 6-11) = deeper variants of those 6 hues. The bright
 * emerald and the dark forest-green BOTH carry initial badges so
 * `Casino Royale (CR)` in bright emerald and `Casino X (CX)` in dark
 * forest are unmistakably different sites at a glance.
 */

const BRIGHT_RING = [
    { pill: "bg-emerald-100 text-emerald-800 border-emerald-300", stripe: "#059669", tint: "rgba(5, 150, 105, 0.16)" },   // 0  green
    { pill: "bg-amber-100 text-amber-800 border-amber-300",       stripe: "#d97706", tint: "rgba(217, 119, 6, 0.18)" },   // 1  yellow-amber
    { pill: "bg-indigo-100 text-indigo-800 border-indigo-300",    stripe: "#4338ca", tint: "rgba(67, 56, 202, 0.15)" },   // 2  blue
    { pill: "bg-fuchsia-100 text-fuchsia-800 border-fuchsia-300", stripe: "#c026d3", tint: "rgba(192, 38, 211, 0.14)" },  // 3  magenta
    { pill: "bg-slate-200 text-slate-700 border-slate-400",       stripe: "#475569", tint: "rgba(71, 85, 105, 0.14)" },   // 4  cool grey
    { pill: "bg-stone-200 text-stone-800 border-stone-400",       stripe: "#78350f", tint: "rgba(120, 53, 15, 0.16)" },   // 5  brown/sienna
];

const DARK_RING = [
    { pill: "bg-emerald-200 text-emerald-900 border-emerald-500", stripe: "#064e3b", tint: "rgba(6, 78, 59, 0.20)" },     // 6  forest green
    { pill: "bg-amber-300 text-amber-900 border-amber-500",       stripe: "#78350f", tint: "rgba(120, 53, 15, 0.20)" },   // 7  deep amber (rust-ish)
    { pill: "bg-indigo-200 text-indigo-900 border-indigo-500",    stripe: "#1e1b4b", tint: "rgba(30, 27, 75, 0.22)" },    // 8  navy
    { pill: "bg-fuchsia-200 text-fuchsia-900 border-fuchsia-500", stripe: "#701a75", tint: "rgba(112, 26, 117, 0.18)" },  // 9  plum
    { pill: "bg-slate-300 text-slate-800 border-slate-500",       stripe: "#1e293b", tint: "rgba(30, 41, 59, 0.18)" },    // 10 charcoal
    { pill: "bg-stone-300 text-stone-900 border-stone-500",       stripe: "#44403c", tint: "rgba(68, 64, 60, 0.20)" },    // 11 deep brown
];

const PALETTE = [...BRIGHT_RING, ...DARK_RING];

const NEUTRAL = {
    pill: "bg-slate-100 text-slate-500 border-slate-200",
    stripe: "transparent",
    tint: "transparent",
    initial: null,
};

/**
 * Build a `name → palette entry` lookup from a list of items that carry
 * a `website_name` field. The initial badge is ALWAYS attached (even
 * when only one site is present) — the letter code is the primary
 * identity in tight visual situations; the colour is the scan-from-a-
 * distance reinforcement.
 *
 * For 13+ sites colours wrap around but the initial code keeps them
 * unique.
 */
export function buildWebsiteColorMap(items) {
    const names = new Set();
    for (const it of items || []) {
        if (it && typeof it.website_name === "string" && it.website_name.trim()) {
            names.add(it.website_name.trim());
        }
    }
    const sorted = [...names].sort((a, b) => a.localeCompare(b));
    const lookup = new Map();
    sorted.forEach((name, i) => {
        const base = PALETTE[i % PALETTE.length];
        lookup.set(name, { ...base, initial: makeInitial(name) });
    });
    return (name) => {
        if (!name || typeof name !== "string") return NEUTRAL;
        return lookup.get(name.trim()) || NEUTRAL;
    };
}

/**
 * Two-letter uppercase initial for a website name.
 *   "Casino Royale"   → "CR"
 *   "Cricket Bet 1"   → "CB"
 *   "Acme"            → "AC"
 *   "Bet1"            → "BE"
 */
function makeInitial(name) {
    const words = name.trim().split(/\s+/).filter(Boolean);
    if (words.length >= 2) return (words[0][0] + words[1][0]).toUpperCase();
    const w = words[0] || "";
    return (w.slice(0, 2) || "??").toUpperCase();
}
